import { type ComponentType, createContext, memo, type ReactNode, useContext, useMemo } from 'react';

import { type GetByShortIDCollectionResponse, type PublicClip } from '@air/api';
import { type Board, type Clip, type PublicBoard } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type GalleryViewProps } from '~/components/v2/components/shared/gallery/GalleryView';
import { type ShowcaseContentBlockBase } from '~/components/v2/components/showcase/showcaseContentBlocks';
import { type PublicBoardContextValue } from '~/providers/PublicBoardProvider';
import { type UseGalleryMixedData, type UseGalleryViewItemsReturn } from '~/swr-hooks/gallery/types';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export type PublicShowcaseLayoutBlockRenderProps = {
  block: ShowcaseContentBlockBase;
  /** The block's public renderer output — null for block types whose renderer hasn't shipped yet. */
  children: ReactNode;
};

export type PublicShowcaseLayoutCoverRenderProps = {
  /** The rendered cover image band (the active page's hero box). */
  children: ReactNode;
};

/**
 * The page the viewer is on. `null` is Home/the link's root page — the ONE designed absence. Both
 * identities travel together because a sub-page's `collectionId` can be shared by two pages (only
 * the slug distinguishes their routes), while the editor addresses pages by `collectionId` alone.
 */
export type PublicShowcaseLayoutActivePage = {
  pageId: string;
  pageSlug: string;
} | null;

export type PublicShowcaseLayoutPageHrefParams = {
  /** The page's canonical slug — `null` for Home/the root page. */
  pageSlug: string | null;
};

export type PublicShowcaseLayoutBoardHrefParams = {
  boardId: string;
  /** The listing page's canonical slug, carried as the route's `?page={slug}` trail — `null` for Home. */
  pageSlug: string | null;
};

export type PublicShowcaseLayoutGalleryViewItemsParams = {
  shortId: string;
};

export type PublicShowcaseLayoutNavPageBoardsParams = {
  /** The page's own collection, or `null` for the ROOT page (the share link's own collection). */
  pageCollectionId: string | null;
};

export type PublicShowcaseLayoutNavPageBoards = {
  boardCount: number;
  boards: Board[];
  isLoadingBoards: boolean;
};

export type PublicShowcaseLayoutUseGalleryViewItems = (
  params: PublicShowcaseLayoutGalleryViewItemsParams,
) => UseGalleryViewItemsReturn<Board, Clip>;

export type PublicShowcaseLayoutUseNavPageBoards = (
  params: PublicShowcaseLayoutNavPageBoardsParams,
) => PublicShowcaseLayoutNavPageBoards;

export type PublicShowcaseLayoutUseBoardGalleryViewItems = () => UseGalleryViewItemsReturn<Board, Clip>;

export type PublicShowcaseLayoutUseBoardTableViewItems = () => UseGalleryMixedData<PublicBoard, PublicClip>;

const emptyGalleryViewItems = (): UseGalleryViewItemsReturn<Board, Clip> => ({
  data: {
    boards: { items: [], isLoading: false, total: 0, hasMore: false },
    clips: { items: [], isLoading: false, total: 0, hasMore: false },
    files: { items: [], isLoading: false, total: 0, hasMore: false },
  },
  isEmpty: true,
  isLoading: false,
  loadNextPage: () => {},
});

const emptyNavPageBoards = (): PublicShowcaseLayoutNavPageBoards => ({
  boardCount: 0,
  boards: [],
  isLoadingBoards: false,
});

const emptyBoardTableViewItems = (): UseGalleryMixedData<PublicBoard, PublicClip> => ({
  data: [],
  isEmpty: true,
  isLoading: false,
  isLoadingMore: false,
  hasMore: false,
  total: 0,
  loadNextPage: () => {},
});

export type PublicShowcaseContextValue = {
  /**
   * The collection record the mounted collection tree renders — the subset the tree's own leaves
   * read. Carried by context (not fetched) so the leaves stay surface-agnostic: PublicShowcaseLayout
   * and ShowcaseEditorLayout mount this provider with whatever record their surface resolved (the
   * live /a page's public fetch, the editor canvas's and Preview iframe's private fetch), and no
   * leaf re-fetches or knows which surface it is on.
   */
  collection: Pick<GetByShortIDCollectionResponse['data'], 'id' | 'name'>;
  /**
   * Wraps each authored content block. The public implementation renders a hidden block as
   * nothing; the editor's shows every block inside its edit shell (visibility chrome included).
   */
  renderBlock: (props: PublicShowcaseLayoutBlockRenderProps) => ReactNode;
  /**
   * Wraps the rendered cover image band. Viewers pass the cover through bare; the editor's
   * Edit-tab canvas wraps it in its cover-authoring chrome (the Change/Reposition/Remove hover
   * toolbar). Never called on a coverless page — the cover renders nothing there, so there is
   * nothing to wrap (and no authoring chrome).
   */
  renderCover: (props: PublicShowcaseLayoutCoverRenderProps) => ReactNode;
  /** The page the viewer is on — `null` for Home/the link's root page. */
  activePage: PublicShowcaseLayoutActivePage;
  /** Navigate the viewer to a page (`null` = Home/the root page). Exits any drilled board. */
  onSelectPage: (page: PublicShowcaseLayoutActivePage) => void;
  /**
   * Builds the href a page entry renders as a real link (middle-click, cmd-click, crawlers).
   * ABSENT when this surface's navigation has no URLs (the collection link editor's Edit tab) —
   * that is its ONE meaning; page entries then render buttons that call `onSelectPage` instead.
   */
  getPageNavigationHref?: (params: PublicShowcaseLayoutPageHrefParams) => string;
  /** The board the viewer is drilled into, or `null` when a page's own body is showing. */
  activeBoardId: string | null;
  /**
   * Navigate the viewer into a board — the collection tree's own drill action (nav board rows,
   * the asset-modal location crumb, and the cascade into the drilled PublicBoard's contract).
   * Same shape as the board tree's member: see PublicBoardProvider.
   */
  onNavigateToBoard: PublicBoardContextValue['onNavigateToBoard'];
  /**
   * Builds the href a nav board entry renders as a real link, carrying the listing page's slug as
   * the `?page={slug}` breadcrumb trail. Same absence contract as `getPageNavigationHref`: ABSENT
   * means this surface's navigation has no URLs, and board entries render buttons instead.
   */
  getBoardNavigationHref?: (params: PublicShowcaseLayoutBoardHrefParams) => string;
  /** The surface's gallery board-card renderer (the public card, or the editor's card twin). */
  renderBoard: GalleryViewProps<Board, Clip>['renderBoard'];
  /** The surface's gallery asset-card renderer (the public card, or the editor's card twin). */
  renderAsset: GalleryViewProps<Board, Clip>['renderAsset'];
  /** The surface's gallery file-card renderer (the public card, or the editor's card twin). */
  renderFile: GalleryViewProps<Board, Clip>['renderFile'];
  /** The surface's selection bar for multi-select actions. */
  SelectionBar: ComponentType;
  /**
   * Whether a block with nothing to show still occupies space on this surface. False on the viewer
   * surfaces, where an empty listing block and a hidden block both render literally nothing; true in
   * the editor's Edit-tab canvas, which wraps EVERY block in its edit shell so the author can work
   * on a block before it has content.
   *
   * The blocks host reads it to decide whether a stack of empty listing blocks means "this showcase
   * is empty" (it does for a viewer — nothing is on the page — and it does not for the author, who
   * is looking at the shells). Stated by each surface rather than inferred, because the same blocks
   * genuinely look different on the two.
   */
  rendersEmptyBlocks: boolean;
  /**
   * Whether this render owns the browser document. True on the real page and the preview iframe
   * (whose document IS the page): body background, forced document theme, the SEO title, and
   * viewport (h-screen) sizing all apply. False in the editor's Edit-tab canvas, which renders
   * inside the editor's document and sizes to its container (theme is scoped via data-theme there —
   * see ShowcaseEditorInlineCanvas).
   */
  ownsDocument: boolean;
  /**
   * Page listings (Boards / Assets / Files blocks + search). Each surface supplies its own hook:
   * live `/a` uses ShortUrl (`usePublicShowcaseGalleryViewItems`); Edit + Preview use workspace
   * Collections / Clips.search (`useShowcaseEditorGalleryViewItems`). The tree calls this — it never
   * branches on which surface it is on (FOUND-840).
   */
  useGalleryViewItems: PublicShowcaseLayoutUseGalleryViewItems;
  /**
   * Boards one nav page holds (sticky bar + mobile menu). Same composition as `useGalleryViewItems`:
   * live `/a` ShortUrl, Edit + Preview workspace.
   */
  useNavPageBoards: PublicShowcaseLayoutUseNavPageBoards;
  /**
   * Gallery items of a board drilled inside this showcase. Live `/a` ShortUrl children; Edit +
   * Preview workspace children.
   */
  useBoardGalleryViewItems: PublicShowcaseLayoutUseBoardGalleryViewItems;
  /**
   * Table items of a board drilled inside this showcase. Same composition as
   * `useBoardGalleryViewItems`.
   */
  useBoardTableViewItems: PublicShowcaseLayoutUseBoardTableViewItems;
};

const defaultContextValue: PublicShowcaseContextValue = {
  collection: { id: '', name: '' },
  renderBlock: () => null,
  renderCover: () => null,
  activePage: null,
  onSelectPage: () => {},
  activeBoardId: null,
  onNavigateToBoard: () => Promise.resolve(),
  // The card-renderer signatures return an element, so the sentinel's members render an empty
  // fragment — never shown, since reading this sentinel throws (dev) or reports (prod) below.
  renderBoard: () => <></>,
  renderAsset: () => <></>,
  renderFile: () => <></>,
  SelectionBar: () => null,
  ownsDocument: false,
  rendersEmptyBlocks: false,
  useGalleryViewItems: emptyGalleryViewItems,
  useNavPageBoards: emptyNavPageBoards,
  useBoardGalleryViewItems: emptyGalleryViewItems,
  useBoardTableViewItems: emptyBoardTableViewItems,
};

const PublicShowcaseContext = createContext<PublicShowcaseContextValue>(defaultContextValue);

/*
 * The collection tree's composition contract — its members are each layout's own REQUIRED props,
 * and PublicShowcaseLayout / ShowcaseEditorLayout mount this provider internally (the AssetModal
 * convention): mounting a layout without a surface's bindings is a type error, so the strip test
 * lives in the compiler. This is the collection tree's deep-composition API. Narrower on purpose
 * than the viewer-wide asset-modal navigation contract (PublicAssetModalNavigationProvider):
 * sub-pages, the drill ACTION and its GATE (activeBoardId decides whether the drilled board or the
 * page body shows), and the nav rows' board hrefs are the COLLECTION tree's own features — delete
 * collections and these members have no other tenant. Board/clip share links never supplied a
 * meaningful value for any of them.
 *
 * The tree itself is surface-blind: shared collection components read this contract for their
 * per-surface implementations without knowing which surface they are on. These members used to
 * ride the optional PublicViewerHostProvider as presence branches ("host present = the editor");
 * now every surface supplies them and no shared component asks which surface it is on.
 *
 * | Surface                                 | Supplies                                            |
 * |------------------------------------------|-----------------------------------------------------|
 * | Live /a collection page                  | public block renderer, cover passthrough, public card renderers, |
 * |                                          | PublicBoardsAndAssetsSelectionBar, ownsDocument: true, |
 * |                                          | rendersEmptyBlocks: false, ShortUrl listing/nav/board-drill hooks, |
 * |                                          | URL-backed page navigation (route reads, shallow pushes, real hrefs) |
 * | Preview iframe                           | same viewer chrome as `/a` (PublicShowcaseLayout hardcodes the |
 * |                                          | viewer personality), editor workspace listing/nav/board-drill |
 * | Editor Edit-tab canvas                   | ShowcaseEditorLayout: edit-shell block renderer, cover-authoring |
 * |                                          | shell, editor card twins, ShowcaseEditorCanvasSelectionBar, |
 * |                                          | ownsDocument: false, rendersEmptyBlocks: true, workspace listings, |
 * |                                          | state-backed page navigation (no hrefs — entries render buttons) |
 */
export const usePublicShowcaseContext = () => {
  const ctx = useContext(PublicShowcaseContext);

  if (ctx === defaultContextValue) {
    const error = new Error('usePublicShowcaseContext must be used within PublicShowcaseProvider');

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      error,
      context: 'Public collection layout context not found',
    });
  }

  return ctx;
};

export type PublicShowcaseProviderProps = PublicShowcaseContextValue & {
  children: ReactNode;
};

/**
 * Carries a surface's composition bindings and the resolved collection record into the tree
 * beneath it. Mounted internally by PublicShowcaseLayout (Live + Preview) and ShowcaseEditorLayout
 * (Edit); it stays exported for tests that isolate inner components below the layout. The bindings
 * are passed in as props (rather than built here) so each surface owns its own behavior — this
 * file never imports any surface's code.
 */
export const PublicShowcaseProvider = memo(
  ({
    activeBoardId,
    activePage,
    children,
    collection,
    getBoardNavigationHref,
    getPageNavigationHref,
    onNavigateToBoard,
    onSelectPage,
    ownsDocument,
    useGalleryViewItems,
    useNavPageBoards,
    useBoardGalleryViewItems,
    useBoardTableViewItems,
    renderAsset,
    renderBlock,
    renderBoard,
    renderCover,
    renderFile,
    rendersEmptyBlocks,
    SelectionBar,
  }: PublicShowcaseProviderProps) => {
    const contextValue = useMemo<PublicShowcaseContextValue>(
      () => ({
        activeBoardId,
        activePage,
        collection,
        getBoardNavigationHref,
        getPageNavigationHref,
        onNavigateToBoard,
        onSelectPage,
        ownsDocument,
        useGalleryViewItems,
        useNavPageBoards,
        useBoardGalleryViewItems,
        useBoardTableViewItems,
        renderAsset,
        renderBlock,
        renderBoard,
        renderCover,
        renderFile,
        rendersEmptyBlocks,
        SelectionBar,
      }),
      [
        activeBoardId,
        activePage,
        collection,
        getBoardNavigationHref,
        getPageNavigationHref,
        onNavigateToBoard,
        onSelectPage,
        ownsDocument,
        useGalleryViewItems,
        useNavPageBoards,
        useBoardGalleryViewItems,
        useBoardTableViewItems,
        renderAsset,
        renderBlock,
        renderBoard,
        renderCover,
        renderFile,
        rendersEmptyBlocks,
        SelectionBar,
      ],
    );

    return <PublicShowcaseContext.Provider value={contextValue}>{children}</PublicShowcaseContext.Provider>;
  },
);

PublicShowcaseProvider.displayName = 'PublicShowcaseProvider';
